import { PurchaseOrderDoesntExists } from "@/app/exceptions";
import { CheckResponse, AlpineData, LivewireData, LivewireMethods } from "@/js/interfaces/Components/alpinejs/Cos/SubmitPurchaseOrder";
import { Livewire } from "@nolikein/types-livewire3";
import { createAlpineLivewireComponent } from "@nolikein/typed-alpinejs";

type WireInstance = Livewire.LivewireMagics<LivewireData, LivewireMethods>;

export default {
    name: 'SubmitPurchaseOrder',
    component: (purchaseOrderId: number | null) => createAlpineLivewireComponent<AlpineData, LivewireData, LivewireMethods>({
        displaySubmittingBlock: false,
        isPurchaseOrderCreated: false,
        isPurchaseOrderAlreadyCreated: false,
        isPurchaseOrderUpdated: false,
        isPurchaseOrderAlreadyUpdated: false,
        isPurchaseOrderExists: false,
        pendingSubmitPurchaseOrder: false,
        isUpdating: false,
        errorMessage: "",
        isFormCompleted: false,
        isCosCommentExists: false,
        hasCosUser: false,
        count: 0,
        purchaseOrderId: null,

        init() {
            // Preset the PO id
            this.purchaseOrderId = purchaseOrderId;
        },

        /**
         * The user would like to create/update a purchase order
         */
        wouldLikeToSubmitPo(): void {
            if (this.isUpdating) {

                return;
            }

            // Reset the error message
            this.errorMessage = '';
            this.isPurchaseOrderAlreadyCreated = false;
            this.isPurchaseOrderAlreadyUpdated = false;

            // A click display/Hide the submitting part
            this.displaySubmittingBlock = true;

            // We only perform request when the block is displayed
            if (!this.displaySubmittingBlock) {

                return;
            }

            // The purchase order should be to create...
            if (null === purchaseOrderId) {
                // Prevent multiple PO creations
                if (this.isPurchaseOrderCreated) {
                    this.isPurchaseOrderAlreadyCreated = true;

                    return;
                }

                // Set display as pending
                this.isUpdating = true;

                this.$wire.$call('canCreatePurchaseOrder')
                    .then((data) => this.checkCanCreatePurchaseOrder(data as CheckResponse, this.$wire));

                return;
            }
            // Or to update.

            // Prevent multiple PO creations
            if (this.isPurchaseOrderUpdated) {
                this.isPurchaseOrderAlreadyUpdated = true;

                return;
            }

            // Set display as pending
            this.isUpdating = true;

            this.$wire.$call('canUpdatePurchaseOrder')
                .then((data) => this.checkCanUpdatePurchaseOrder(data as CheckResponse, this.$wire));
        },

        /**
         * Check if you can create a purchase order.
         *
         * @param response
         * @param wire The $wire instance
         */
        checkCanCreatePurchaseOrder(response: CheckResponse, wire: WireInstance): undefined {
            // Update display to show everything is okay
            this.isFormCompleted = response.isFormCompleted ? true : false;
            this.isPurchaseOrderExists = response.isPurchaseOrderExists ? true : false;
            this.hasCosUser = response.hasCosUser ? true : false;
            this.isCosCommentExists = response.isCosCommentExists ? true : false;

            // Update display now
            if (!this.isFormCompleted || !this.hasCosUser) {
                this.isUpdating = false;

                return;
            }

            // Prevent multiple PO creation (from response content)
            if (this.isPurchaseOrderExists) {
                this.isPurchaseOrderAlreadyCreated = true;

                return;
            }

            // We can perform the creation only if we have everything required
            this.callCreatePurchaseOrder(wire);
        },

        /**
         * Ask backend to create the purchase order.
         *
         * @param wire The $wire instance
         */
        callCreatePurchaseOrder(wire: WireInstance): void {
            this.isUpdating = true;

            wire.$dispatch('createPurchaseOrder');
        },

        /**
         * Check if you can create a purchase order.
         *
         * @param response
         * @param wire The $wire instance
         */
        checkCanUpdatePurchaseOrder(response: CheckResponse, wire: WireInstance): undefined {
            // Update display to show everything is okay
            this.isFormCompleted = response.isFormCompleted ? true : false;
            this.isPurchaseOrderExists = response.isPurchaseOrderExists ? true : false;
            this.hasCosUser = response.hasCosUser ? true : false;
            this.isCosCommentExists = response.isCosCommentExists ? true : false;

            // Update display now
            if (!this.isFormCompleted) {
                this.isUpdating = false;

                return;
            }

            // Prevent multiple PO update (from response content)
            if (!this.isPurchaseOrderExists) {
                throw new PurchaseOrderDoesntExists("The purchase order was supposed to be updated, but no purchase order was actually referenced.");
            }

            // We can perform the update only if we have everything required
            this.callUpdatePurchaseOrder(wire);
        },

        /**
         * Called after the purchase order is created.
         */
        onPurchaseOrderCreated(): void {
            this.isUpdating = false;
            this.isPurchaseOrderCreated = true;
            this.$dispatch('refreshPurchaseOrder');
        },

        /**
         * Ask backend to update the purchase order.
         * @param wire The $wire instance
         */
        callUpdatePurchaseOrder(wire: WireInstance): void {
            this.isUpdating = true;

            wire.$dispatch('updatePurchaseOrder');
        },

        /**
         * Called after the purchase order is updated.
         */
        onPurchaseOrderUpdated(): void {
            this.isUpdating = false;
            this.isPurchaseOrderUpdated = true;
            this.$dispatch('refreshPurchaseOrder');
        },
    }),
};
